// frontend/src/pages/admin/ExternalTokens/RequestLogsTab.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  FileSearch, Filter, KeyRound, ListFilter, RefreshCw, X, XCircle,
} from 'lucide-react';

import { useApiRequestLogs, useServiceApiKeys } from '../../../hooks/useApiOperationsData';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { FilterButton } from '../../../components/UI/FilterButton';
import DatePickerField from '../../../components/UI/DatePickerField';
import PillSelect from '../../../components/UI/PillSelect';
import StatusBadge from '../../../components/UI/StatusBadge';
import Pagination from '../../../components/UI/Pagination';
import { formatDateTime } from '../../../utils/dateUtils';

const STATUS_CLASS_OPTIONS = [
  { value: '', label: 'All results' },
  { value: 'success', label: 'Success (2xx/3xx)' },
  { value: 'client_error', label: 'Client error (4xx)' },
  { value: 'server_error', label: 'Server error (5xx)' },
  { value: 'error', label: 'Any error (4xx/5xx)' },
];

const METHOD_OPTIONS = [
  { value: '', label: 'All methods' },
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

const statusBadgeFor = (statusClass, statusCode) => {
  if (statusClass === 'server_error') return { status: 'failed', label: String(statusCode) };
  if (statusClass === 'client_error') return { status: 'on_hold', label: String(statusCode) };
  return { status: 'active', label: String(statusCode) };
};

const RequestLogsTab = ({ initialFilters = {} }) => {
  const [search, setSearch] = useState(initialFilters.search || '');
  const [showFilters, setShowFilters] = useState(
    Boolean(initialFilters.status_class || initialFilters.key_id || initialFilters.method),
  );
  const [statusClass, setStatusClass] = useState(initialFilters.status_class || '');
  const [method, setMethod] = useState(initialFilters.method || '');
  const [keyId, setKeyId] = useState(initialFilters.key_id || '');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    if (initialFilters.status_class) setStatusClass(initialFilters.status_class);
    if (initialFilters.key_id) setKeyId(initialFilters.key_id);
    if (initialFilters.search) setSearch(initialFilters.search);
  }, [initialFilters.status_class, initialFilters.key_id, initialFilters.search]);

  const { data: keysData } = useServiceApiKeys();
  const keyOptions = useMemo(
    () => [
      { value: '', label: 'All keys' },
      ...(keysData?.keys || []).map((k) => ({
        value: String(k.id),
        label: `${k.app_name} (${k.key_prefix})`,
      })),
    ],
    [keysData],
  );

  const params = useMemo(() => ({
    page,
    page_size: pageSize,
    ...(search ? { search } : {}),
    ...(statusClass ? { status_class: statusClass } : {}),
    ...(method ? { method } : {}),
    ...(keyId ? { key_id: keyId } : {}),
    ...(startDate ? { start: startDate.toISOString() } : {}),
    ...(endDate ? { end: endDate.toISOString() } : {}),
  }), [page, pageSize, search, statusClass, method, keyId, startDate, endDate]);

  const { data, isLoading, isFetching, isError, refetch } = useApiRequestLogs(params);

  const activeFilterCount = [statusClass, method, keyId, startDate, endDate].filter(Boolean).length;
  const results = data?.results || [];
  const totalPages = data?.total_pages || 1;
  const totalCount = data?.count || 0;

  const clearFilters = () => {
    setStatusClass('');
    setMethod('');
    setKeyId('');
    setStartDate(null);
    setEndDate(null);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Data-table toolbar: search left · Filter → Refresh → Clear right (DS). */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <DebouncedSearchInput
            value={search}
            onSearch={(value) => { setSearch(value); setPage(1); }}
            placeholder="Search app, endpoint, or key prefix..."
            theme="admin"
            className="w-full sm:max-w-xl sm:flex-1"
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={activeFilterCount}
              onClick={() => setShowFilters((v) => !v)}
              className="flex-shrink-0"
            />
            <button
              type="button"
              onClick={() => refetch()}
              className="icon-button-admin flex-shrink-0"
              title="Refresh"
              aria-label="Refresh request logs"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="icon-button-admin flex-shrink-0"
                title="Clear filters"
                aria-label="Clear filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Result</label>
              <PillSelect
                options={STATUS_CLASS_OPTIONS}
                value={statusClass}
                onChange={(val) => { setStatusClass(val); setPage(1); }}
                theme="admin"
                icon={ListFilter}
                iconColor="text-red-600"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
              <PillSelect
                options={METHOD_OPTIONS}
                value={method}
                onChange={(val) => { setMethod(val); setPage(1); }}
                theme="admin"
                icon={Filter}
                iconColor="text-red-600"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">API key</label>
              <PillSelect
                options={keyOptions}
                value={keyId}
                onChange={(val) => { setKeyId(val); setPage(1); }}
                theme="admin"
                icon={KeyRound}
                iconColor="text-red-600"
                className="w-full"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                <DatePickerField
                  theme="admin"
                  selected={startDate}
                  onChange={(d) => { setStartDate(d); setPage(1); }}
                  maxDate={endDate || new Date()}
                  isClearable
                  placeholderText="From date"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                <DatePickerField
                  theme="admin"
                  selected={endDate}
                  onChange={(d) => { setEndDate(d); setPage(1); }}
                  minDate={startDate || undefined}
                  maxDate={new Date()}
                  isClearable
                  placeholderText="To date"
                />
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Retained {data?.retention_days || 90} days · metadata only — no request/response bodies.
        </p>
      </div>

      {isError ? (
        <div className="text-center py-12">
          <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Couldn&apos;t load request logs</h3>
          <p className="text-sm text-gray-500 mb-4">Try again in a moment.</p>
          <button type="button" onClick={() => refetch()} className="btn btn-admin-primary">
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="px-6 py-4 border-b border-gray-100 flex gap-4">
              <div className="h-4 bg-gray-200 rounded w-16" />
              <div className="h-4 bg-gray-200 rounded flex-1" />
              <div className="h-4 bg-gray-200 rounded w-12" />
            </div>
          ))}
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-12">
          <FileSearch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {activeFilterCount || search ? 'No logs match your filters' : 'No request logs yet'}
          </h3>
          <p className="text-sm text-gray-500">
            {activeFilterCount || search
              ? 'Try clearing search and filters above.'
              : 'Authenticated Service API Key calls will appear here.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">When</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">App / Key</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Request</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Latency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((row) => {
                  const badge = statusBadgeFor(row.status_class, row.status_code);
                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatDateTime(row.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm min-w-0">
                        <p className="font-medium text-gray-900 truncate">{row.app_name}</p>
                        <p className="text-xs font-mono text-gray-500">{row.key_prefix || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-sm min-w-0">
                        <span className="text-xs font-semibold text-gray-500 mr-2">{row.method}</span>
                        <code className="text-xs font-mono text-gray-800 break-all">{row.endpoint}</code>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={badge.status} label={badge.label} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
                        {row.response_time_ms != null ? `${row.response_time_ms}ms` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalCount={totalCount}
            pageSize={pageSize}
            theme="admin"
            noun="requests"
          />
        </div>
      )}
    </div>
  );
};

export default RequestLogsTab;
